import { AlertTriangle, Info } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { StatusBadge } from '@/components/common'
import type { LeaveDecisionContext } from '@/features/leave'
import type { RegularizationDecisionContext } from '@/features/attendance'
import type { TransferDecisionContext } from '@/features/employee'
import type { GenericRequestDecisionContext } from '@/features/request'
import type { ProCaseDecisionContext } from '@/features/pro'
import type { OfferDecisionContext, RequisitionDecisionContext } from '@/features/recruitment'
import type { TrainingNominationDecisionContext } from '@/features/training'
import { LeaveContext } from './contexts/LeaveContext'
import { RegularisationContext } from './contexts/RegularisationContext'
import { TransferContext } from './contexts/TransferContext'
import { GenericRequestContext } from './contexts/GenericRequestContext'
import { ProCaseContext } from './contexts/ProCaseContext'
import { RequisitionContext } from './contexts/RequisitionContext'
import { OfferApprovalContext } from './contexts/OfferApprovalContext'
import { TrainingNominationContext } from './contexts/TrainingNominationContext'
import type { TaskContext } from '../types'

export interface DecisionContextPanelProps {
  context: TaskContext | undefined
  loading?: boolean
}

/**
 * Everything the owning module can say about the thing being approved.
 *
 * <p>The panel switches on `detailKind` — the entity key — and never on the shape of
 * `detail`. Sniffing fields works until two modules both have a `status`, and then it
 * renders the wrong screen for the wrong record, silently.
 *
 * <p>An unrecognised kind is not an error. Most approvable things need no extra context, and
 * saying "nothing further to show" is more honest than an empty panel that looks broken. So
 * is the failure case: when a module could not assemble its detail the reason is shown, and
 * the decision stays available — an approval screen that will not open is worse than one
 * that opens with less on it.
 */
export function DecisionContextPanel({ context, loading }: DecisionContextPanelProps) {
  if (loading) {
    return <Skeleton className="h-40 w-full rounded-[var(--radius-card)]" />
  }
  if (!context) {
    return null
  }

  if (context.unavailableReason) {
    return (
      <Card className="flex items-start gap-2 p-4">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[var(--color-warning)]" aria-hidden="true" />
        <div>
          <p className="text-sm font-medium text-[var(--color-heading)]">
            The supporting detail is not available
          </p>
          <p className="mt-1 text-sm text-[var(--color-muted)]">{context.unavailableReason}</p>
        </div>
      </Card>
    )
  }

  const detail = context.detail
  if (detail == null) {
    return (
      <Card className="flex items-start gap-2 p-4">
        <Info className="mt-0.5 size-4 shrink-0 text-[var(--color-muted)]" aria-hidden="true" />
        <p className="text-sm text-[var(--color-muted)]">
          There is nothing further to show for this kind of approval — the request itself is
          the whole of it.
        </p>
      </Card>
    )
  }

  switch (context.detailKind) {
    case 'leave.request':
      return <LeaveContext detail={detail as LeaveDecisionContext} />
    case 'attendance.regularization':
      return <RegularisationContext detail={detail as RegularizationDecisionContext} />
    case 'employee.transfer':
      return <TransferContext detail={detail as TransferDecisionContext} />
    case 'request.generic':
      return <GenericRequestContext detail={detail as GenericRequestDecisionContext} />
    case 'pro.case':
      return <ProCaseContext detail={detail as ProCaseDecisionContext} />
    case 'job_requisition':
      return <RequisitionContext detail={detail as RequisitionDecisionContext} />
    case 'offer':
      return <OfferApprovalContext detail={detail as OfferDecisionContext} />
    case 'training_nomination':
      return <TrainingNominationContext detail={detail as TrainingNominationDecisionContext} />
    default:
      // A kind this build does not know how to render. The task is still decidable, and
      // saying which kind it is beats rendering nothing at all.
      return (
        <Card className="flex items-start gap-2 p-4">
          <Info className="mt-0.5 size-4 shrink-0 text-[var(--color-muted)]" aria-hidden="true" />
          <p className="text-sm text-[var(--color-muted)]">
            This approval is a{' '}
            <StatusBadge status={context.detailKind ?? 'unknown'} tone="neutral" />, which this
            screen does not have a detailed view for yet. Open the record itself before deciding.
          </p>
        </Card>
      )
  }
}
